import { useEffect, useMemo, useState } from 'react'
import api from '../api/axios'
import { ClipboardDocumentListIcon, ArrowPathIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'

interface AuditLog {
  id: string
  admin_email: string
  admin_name?: string
  action_type: string
  resource_type: string
  resource_name?: string
  action_details?: string
  success: boolean
  error_message?: string
  created_at: string
}

export default function AuditLogs() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionFilter, setActionFilter] = useState('all')
  const [resourceFilter, setResourceFilter] = useState('all')
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    fetchLogs()
  }, [actionFilter, resourceFilter])

  const fetchLogs = async () => {
    setLoading(true)
    setError('')
    try {
      // Stack Auth tokens are automatically attached by axios interceptor via cookies
      const response = await api.get('/super-admin/audit/logs', {
        params: {
          action_type: actionFilter !== 'all' ? actionFilter : undefined,
          resource_type: resourceFilter !== 'all' ? resourceFilter : undefined,
          limit: 200
        }
      })
      setLogs(response.data.logs || [])
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }

  const filteredLogs = useMemo(() => {
    const text = searchText.toLowerCase()
    return logs.filter((log) => {
      if (text) {
        const blob = [log.admin_email, log.admin_name, log.resource_name, log.action_details, log.error_message]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!blob.includes(text)) return false
      }
      return true
    })
  }, [logs, searchText])

  const exportCsv = () => {
    if (filteredLogs.length === 0) return
    const headers = ['When', 'Admin Email', 'Admin Name', 'Action', 'Resource Type', 'Resource Name', 'Details', 'Status', 'Error']
    const escape = (v: string) => `"${(v ?? '').replace(/"/g, '""')}"`
    const rows = filteredLogs.map(l => [
      new Date(l.created_at).toISOString(),
      l.admin_email,
      l.admin_name ?? '',
      l.action_type,
      l.resource_type,
      l.resource_name ?? '',
      typeof l.action_details === 'string' ? l.action_details : JSON.stringify(l.action_details ?? ''),
      l.success ? 'Success' : 'Failed',
      l.error_message ?? '',
    ].map(c => escape(String(c))).join(','))
    const csv = [headers.map(escape).join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const formatDetails = (details?: unknown) => {
    if (!details) return null
    if (typeof details === 'string') return details
    try {
      return JSON.stringify(details, null, 2)
    } catch (e) {
      return String(details)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardDocumentListIcon className="w-7 h-7 text-blue-600" />
              Audit Logs
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">System activity and security events (latest 200)</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} disabled={loading || filteredLogs.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArrowDownTrayIcon className="w-4 h-4" /> Export
            </button>
            <button onClick={fetchLogs} disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArrowPathIcon className="w-4 h-4" /> {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" /> {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Search email, name, resource, details" value={searchText} onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
          </div>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="all">All actions</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="enable_feature">Enable Feature</option>
            <option value="disable_feature">Disable Feature</option>
            <option value="execute_query">Execute Query</option>
            <option value="backup_database">Backup Database</option>
            <option value="manage_user">Manage User</option>
            <option value="view">View</option>
          </select>
          <select value={resourceFilter} onChange={(e) => setResourceFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
            <option value="all">All resources</option>
            <option value="company">Company</option>
            <option value="feature_flag">Feature Flag</option>
            <option value="user">User</option>
            <option value="database">Database</option>
            <option value="report">Report</option>
            <option value="alert">Alert</option>
            <option value="system_metric">System Metric</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" /><p className="text-sm text-slate-500">Loading audit logs…</p></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardDocumentListIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">No audit entries found.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  {['When', 'Admin', 'Action', 'Resource', 'Details', 'Status'].map(h => (
                    <th key={h} className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">{log.admin_name || 'Unknown'}</div>
                      <div className="text-xs text-slate-400">{log.admin_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700">{log.action_type}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-700">{log.resource_type}</div>
                      {log.resource_name && <div className="text-xs text-slate-400">{log.resource_name}</div>}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {log.action_details && <pre className="text-xs text-slate-500 whitespace-pre-wrap font-mono bg-slate-50 rounded p-1.5 max-h-24 overflow-auto">{formatDetails(log.action_details)}</pre>}
                      {log.error_message && <div className="text-xs text-red-500 mt-1">{log.error_message}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${log.success ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {log.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
